import { DSBottomSheet } from '@repo/design-system/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { useState } from 'react'
import Slider from 'react-slick'
import { GTM_CLICK_EVENT, useEventTracking } from '../../../helpers/gtmHelper'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import { buildSanityImage } from '../../../modules/utils'
import { PRICING_BANNER_LIST_QUERY } from '../../../sanity/groqQueries'
import { PRICING_BANNER_LIST_QUERYResult } from '../../../sanity/types'
import { useBasicPopup } from '../../basic/popup/BasicPopup'
import SliderNextBtn from '../slider/SliderNextBtn'
import SliderPrevBtn from '../slider/SliderPrevBtn'

export interface IBannerData {
  _id: string
  title: string
  slug: Slug
  title123: string
  _createdAt: Date
  _rev: string
  pricingBannrListEn: PricingBannrList[]
  pricingBannrListKo: PricingBannrList[]
  _type: string
  _updatedAt: Date
}

export interface PricingBannrList {
  bannerImage: Image | any
  _type: string
  _key: string
  detailImage: Image
}

export interface Image {
  _type: BannerImageType
  alt: Alt
  asset: Asset
}

export enum BannerImageType {
  Image = 'image',
}

export enum Alt {
  Test = 'test',
  Text = 'text',
}

export interface Asset {
  _ref: string
  _type: AssetType
}

export enum AssetType {
  Reference = 'reference',
}

export interface Slug {
  current: string
  _type: string
}

const fetchBanner = async () => {
  const params = {
    slug: 'pricing_banner',
  }
  const response: PRICING_BANNER_LIST_QUERYResult = await client.fetch(PRICING_BANNER_LIST_QUERY, params)
  return response
}

const useBannerData = () => {
  return useQuery({
    queryKey: ['banner_list'],
    queryFn: () => fetchBanner(),
  })
}

const PricingBannerListSection = () => {
  const isMobile = useIsMobile()
  const basicPopup = useBasicPopup()
  const { locale } = useTranslation()
  const { sendClickEvent } = useEventTracking()

  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false)
  const [bottomSheetBannerData, setBottomSheetBannerData] = useState<any>()
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0)

  const { data: bannerData, isFetching } = useBannerData()

  const bannerList = bannerData?.[locale]

  const handleClickBanner = (banner) => {
    sendClickEvent({ button_name: GTM_CLICK_EVENT.PURCHASE_BANNER })

    if (isMobile) {
      setBottomSheetBannerData(banner)
      setShowBottomSheet(true)
    } else {
      basicPopup.show({
        wrapperClassName: 'rounded-[12px]',
        id: 'banner',
        overlayClose: true,
        noTitle: true,
        bodyPx: 0,
        bodyPb: 0,
        maxW: 560,
        body: (
          <div className="w-full h-full relative rounded-[12px]">
            <div className="sticky h-[80px] bg-white top-[0px] z-1 w-full"></div>
            <img src={buildSanityImage(banner?.detailImage).url()} />
          </div>
        ),
      })
    }
  }
  return (
    <div className="flex flex-col mt-[32px] sm:mt-[48px] px-[20px] sm:px-[0px]">
      {!isFetching && bannerData && (
        <div className="w-full overflow-">
          <Slider
            dots={!isMobile}
            infinite={true}
            slidesToShow={isMobile ? 1.05 : 3}
            slidesToScroll={1}
            draggable={true}
            lazyLoad={'ondemand'}
            nextArrow={
              !isMobile && (
                <SliderNextBtn
                  width={isMobile ? '30px' : null}
                  height={isMobile ? '30px' : null}
                  translateX={20}
                  top={'52px'}
                  right={isMobile ? '12px' : '2px'}
                  onClick={() => {
                    sendClickEvent({ button_name: 'pricing_banner_list_arrow' })

                    setActiveItemIndex((prev) => prev + 1)
                  }}
                />
              )
            }
            prevArrow={
              !isMobile && (
                <SliderPrevBtn
                  top={'52px'}
                  width={isMobile ? '30px' : null}
                  height={isMobile ? '30px' : null}
                  onClick={() => {
                    sendClickEvent({ button_name: 'pricing_banner_list_arrow' })

                    setActiveItemIndex((prev) => prev - 1)
                  }}
                />
              )
            }
            className="triple-slider-pricing sm:mb-[20px] relative"
            afterChange={(index) => setActiveItemIndex(index)}
            customPaging={(i) => {
              return (
                <div
                  className={classNames(`mx-auto mt-[8px] h-6 rounded-full bg-gray-200 `, {
                    'w-[12px] bg-gray-800': i == activeItemIndex,
                    'w-[6px] bg-gray-200': i != activeItemIndex,
                  })}
                ></div>
              )
            }}
          >
            {bannerList?.map((banner, i) => {
              return (
                <div
                  key={i}
                  className="cursor-pointer outline-none sm:px-[10px] sm:pl-0 ml-10 pl-8"
                  onClick={() => handleClickBanner(banner)}
                >
                  <img src={buildSanityImage(banner?.bannerImage).url()} />
                </div>
              )
            })}
          </Slider>
        </div>
      )}
      {showBottomSheet && (
        <DSBottomSheet id="DsBottomSheet" isOpen={showBottomSheet} setIsOpen={setShowBottomSheet} closeButton={true}>
          <div className="w-full relative overflow-scroll h-[calc(100vh_-_158px)] mt-[54px]">
            <img src={buildSanityImage(bottomSheetBannerData?.detailImage).url()} />
          </div>
        </DSBottomSheet>
      )}
    </div>
  )
}

export default PricingBannerListSection
